import { useRef, type ReactNode } from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { RiftboundButton } from '../ui/button';
import { menuItemHoverBg, menuItemSelectedBg, menuItemSelectedHoverBg } from '../../theme/recipes/button';
import { dropdownBg, raisedShadow } from '../../theme/themeColor';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MdEditNote, MdExpandMore, MdGroups } from 'react-icons/md';
import { useAuth } from '../../hooks/useAuth';
import DeckIcon from '../../images/deck-icon.svg?react';
import { NavSubmenuPortal, useNavSubmenuHover } from './NavSubmenuPortal';

function DeckSubmenuLink({
  to,
  icon,
  active,
  children,
}: {
  to: string;
  icon: ReactNode;
  active: boolean;
  children: string;
}) {
  return (
    <RouterLink to={to} style={{ display: 'block', textDecoration: 'none', outline: 'none' }}>
      <Flex
        role="menuitem"
        align="center"
        gap={2.5}
        px={3}
        py={2}
        fontSize="sm"
        fontWeight={active ? 'semibold' : 'medium'}
        color={active ? 'accent' : 'text'}
        bg={active ? menuItemSelectedBg : 'transparent'}
        transitionProperty="background-color, color"
        transitionDuration="0.12s"
        _hover={{
          bg: active ? menuItemSelectedHoverBg : menuItemHoverBg,
          color: active ? 'accent' : 'text',
        }}
      >
        <Box
          display="inline-flex"
          boxSize={4}
          color={active ? 'accent' : 'textMuted'}
          flexShrink={0}
          aria-hidden
          css={{ '& > svg': { width: '100%', height: '100%' } }}
        >
          {icon}
        </Box>
        {children}
      </Flex>
    </RouterLink>
  );
}

export interface DeckNavSubmenuProps {
  /** Returns whether the given pathname belongs to the decks section (for trigger active styling). */
  navMatch: (pathname: string) => boolean;
}

/** Desktop nav: Decks trigger + hover submenu (mobile uses bottom nav only). */
export function DeckNavSubmenu({ navMatch }: DeckNavSubmenuProps) {
  const location = useLocation();
  const { user } = useAuth();
  const hover = useNavSubmenuHover();
  const anchorRef = useRef<HTMLDivElement>(null);

  const pathname = location.pathname;
  const deckSectionActive = navMatch(pathname);

  const mineActive = pathname === '/decks/my-decks';
  const communityActive = pathname === '/decks';
  const builderActive =
    pathname === '/decks/new' || (pathname.startsWith('/decks/') && pathname.endsWith('/edit'));

  return (
    <Box
      ref={anchorRef}
      position="relative"
      onMouseEnter={hover.show}
      onMouseLeave={hover.hide}
    >
      <RiftboundButton
        as={RouterLink as never}
        {...{ to: '/decks' } as Record<string, unknown>}
        treatment="ghost"
        size="sm"
        variant={deckSectionActive ? 'primary' : undefined}
        px={{ md: 2, xl: 3 }}
        h="auto"
        py={1.5}
        aria-haspopup="menu"
        aria-expanded={hover.open}
      >
        <Flex align="center" gap={1} lineHeight={1.15}>
          <Text as="span" fontSize="sm">
            Decks
          </Text>
          <Icon as={MdExpandMore} boxSize={3.5} opacity={0.75} pointerEvents="none" aria-hidden />
        </Flex>
      </RiftboundButton>

      <NavSubmenuPortal
        open={hover.open}
        anchorRef={anchorRef}
        label="Decks submenu"
        minW="232px"
        onMouseEnter={hover.show}
        onMouseLeave={hover.hide}
      >
          <Box
            bg={dropdownBg}
            borderWidth="hairline"
            borderColor="border"
            rounded="md"
            py={1}
            boxShadow={raisedShadow}
          >
            {user && (
              <DeckSubmenuLink to="/decks/my-decks" icon={<DeckIcon />} active={mineActive}>
                My Decks
              </DeckSubmenuLink>
            )}
            <DeckSubmenuLink to="/decks" icon={<Icon as={MdGroups} boxSize={4} />} active={communityActive}>
              Community Decks
            </DeckSubmenuLink>
            <DeckSubmenuLink to="/decks/new" icon={<Icon as={MdEditNote} boxSize={4} />} active={builderActive}>
              Deck Builder
            </DeckSubmenuLink>
          </Box>
      </NavSubmenuPortal>
    </Box>
  );
}
